import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const BADGE_BASE =
  "inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden";

const badgeVariants = cva(BADGE_BASE, {
  variants: {
    variant: {
      default:
        "border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary/90",
      secondary:
        "border-transparent bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90",
      destructive:
        "border-transparent bg-destructive text-white [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
      outline:
        "text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
    },
  },
});

/**
 * The status-color contract's tone system (one hue = one meaning). Each tone
 * has a `solid` form (white text — verdict emphasis, one per view) and a
 * `soft` form (tinted bg + dark fg + tonal border — counts, chips). Backed by
 * the `--color-tone-*` tokens in `index.css`. Prefer `StatusBadge` over
 * hand-picking a tone at a call site.
 */
export type BadgeTone =
  | "positive"
  | "positiveStrong"
  | "caution"
  | "cautionStrong"
  | "info"
  | "negative"
  | "active"
  | "muted";

const TONE_CLASSES: Record<BadgeTone, { solid: string; soft: string }> = {
  positive: {
    solid: "border-transparent bg-tone-positive-solid text-white",
    soft: "bg-tone-positive-soft-bg text-tone-positive-soft-fg border-tone-positive-soft-border",
  },
  positiveStrong: {
    solid: "border-transparent bg-tone-positive-strong text-white",
    soft: "bg-tone-positive-soft-bg text-tone-positive-soft-fg border-tone-positive-soft-border",
  },
  caution: {
    solid: "border-transparent bg-tone-caution-solid text-white",
    soft: "bg-tone-caution-soft-bg text-tone-caution-soft-fg border-tone-caution-soft-border",
  },
  cautionStrong: {
    solid: "border-transparent bg-tone-caution-strong text-white",
    soft: "bg-tone-caution-soft-bg text-tone-caution-soft-fg border-tone-caution-soft-border",
  },
  info: {
    solid: "border-transparent bg-tone-info-solid text-white",
    soft: "bg-tone-info-soft-bg text-tone-info-soft-fg border-tone-info-soft-border",
  },
  negative: {
    solid: "border-transparent bg-tone-negative-solid text-white",
    soft: "bg-tone-negative-soft-bg text-tone-negative-soft-fg border-tone-negative-soft-border",
  },
  active: {
    solid: "border-transparent bg-tone-active-solid text-white",
    soft: "bg-tone-active-soft-bg text-tone-active-soft-fg border-tone-active-soft-border",
  },
  muted: {
    solid: "border-transparent bg-brand-slate text-white",
    soft: "bg-tone-muted-bg text-tone-muted-fg border-tone-muted-border font-normal",
  },
};

function Badge({
  className,
  variant,
  tone,
  soft = false,
  pill = false,
  asChild = false,
  ...props
}: React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & {
    asChild?: boolean;
    /** A status-contract tone. When set, it replaces `variant`. */
    tone?: BadgeTone;
    /** Soft (tinted) form of the tone; solid otherwise. Ignored without `tone`. */
    soft?: boolean;
    /** Fully-rounded pill (count chips) instead of the default radius. */
    pill?: boolean;
  }) {
  const Comp = asChild ? Slot : "span";
  const color = tone
    ? cn(BADGE_BASE, TONE_CLASSES[tone][soft ? "soft" : "solid"])
    : badgeVariants({ variant: variant ?? "default" });

  return (
    <Comp
      data-slot="badge"
      className={cn(color, pill && "rounded-full", className)}
      {...props}
    />
  );
}

export { Badge, badgeVariants };
